import React from "react";
import Cta from "@qlik/qlik/src/components/Cta";
import Media from "@qlik/qlik/src/components/Media";
import { getSpacingClassName } from "@qlik/qlik/src/lib/util/spacing";
import { getColorClassName } from "@qlik/qlik/src/lib/util/colors";
import { getCardBodyContent, getCardEyebrowText, renderSanitizedHtmlContent } from "@qlik/qlik/src/lib/util/helpers";
import * as styles from "./styles.module.css";

/**
 * HeroBannerCenterImage component.
 * Renders a promotional card with title, description, body, cta, and media.
 * @param {Object} props - The component props.
 * @param {Object} props.data - The data for the card.
 * @returns {JSX.Element} The rendered component.
 */
const HeroBannerCenterImage = ({ data }) => {
    const { cardItem, additionalConfiguration, backgroundColor, customId } = data || {};
    const { title, cta, media } = cardItem || {};
    const cardEyebrowText = cardItem && getCardEyebrowText(cardItem);
    const sectionSpacingClassName = getSpacingClassName(additionalConfiguration, "pt-10 md:pt-20 lg:pt-[130px]");
    const { sectionColorMapping } = getColorClassName(backgroundColor);

    const bodyContent = getCardBodyContent(cardItem);

    return (
        <section
            className={`overflow-hidden ${sectionSpacingClassName} ${sectionColorMapping.bgColor || "bg-transparent"} `}
            id={customId}
        >
            <div className="container">
                <div className="row">
                    <div className="col-12 relative">
                        {cardEyebrowText && (
                            <p className="tag-2 uppercase text-sm mt-5 mb-3 text-center mx-auto">{cardEyebrowText}</p>
                        )}
                        {title && (
                            <h1
                                className="text-with-color text-center mb-3.75 lg:mb-7.5 title-1"
                                // eslint-disable-next-line react/no-danger
                                dangerouslySetInnerHTML={{ __html: renderSanitizedHtmlContent(title) }}
                            />
                        )}

                        {/* Rendering the description if it exists */}
                        {bodyContent && (
                            <div className={`${styles.bodyContent} xl:max-w-[1030px] xl:mx-auto`}>{bodyContent}</div>
                        )}

                        {/* Rendering the CTA if it exists */}
                        {cta?.map((button) => (
                            <div className="flex flex-wrap gap-2 justify-center mt-7 xl:mt-12.25" key={button.id}>
                                <Cta key={button.id} data={button} className="py-3.75 tracking-normal" />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            {/* Rendering the media if it exists */}
            {(media?.[0]?.desktopMedia.length || media?.[0]?.mobileMedia.length) > 0 && (
                <div className="col-12 relative pb-1.25 mt-10 xl:mt-[121px] xl:pb-0">
                    <div>
                        <Media
                            media={media[0]}
                            className="object-contain max-w-none w-[660px] sm:w-268.75 relative -translate-x-2/4 left-2/4 md:w-[1228px] lg:object-cover lg:w-[1743px] xl:w-[2085px]"
                        />
                    </div>
                </div>
            )}
        </section>
    );
};

export default HeroBannerCenterImage;
